import React from 'react';
import { resolvePublicFileUrl } from '../../utils/imageUrls';

export interface HomeStripImage {
  id: number | string;
  image_url?: string | null;
  alt?: string;
}

interface HomeImageStripProps {
  images: HomeStripImage[];
  /** Max tiles to show */
  max?: number;
  className?: string;
  /** Aspect / density */
  size?: 'sm' | 'md';
}

/**
 * Quiet photo strip for signed-out homepage proof sections.
 * Skips entries without images; returns null when empty.
 */
export const HomeImageStrip: React.FC<HomeImageStripProps> = ({
  images,
  max = 4,
  className = '',
  size = 'md',
}) => {
  const tiles = images
    .map((img) => ({
      id: img.id,
      url: resolvePublicFileUrl(img.image_url),
      alt: img.alt || '',
    }))
    .filter((t) => Boolean(t.url))
    .slice(0, max);

  if (tiles.length === 0) return null;

  const height = size === 'sm' ? 'h-24 sm:h-28' : 'h-28 sm:h-36';

  return (
    <div
      className={`grid gap-2 sm:gap-3 ${
        tiles.length === 1
          ? 'grid-cols-1'
          : tiles.length === 2
            ? 'grid-cols-2'
            : tiles.length === 3
              ? 'grid-cols-3'
              : 'grid-cols-2 sm:grid-cols-4'
      } ${className}`.trim()}
      aria-hidden={tiles.every((t) => !t.alt)}
    >
      {tiles.map((tile) => (
        <div
          key={tile.id}
          className={`relative overflow-hidden rounded-lg bg-[#0a0a0a] ${height}`}
        >
          <img
            src={tile.url!}
            alt={tile.alt}
            className="w-full h-full object-cover motion-safe:transition-transform motion-safe:duration-500 motion-safe:hover:scale-[1.03]"
            loading="lazy"
          />
        </div>
      ))}
    </div>
  );
};

export default HomeImageStrip;
